import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import type { PaginatedResult } from '../type';

type UseInfiniteQueryWithLimitProps<T> = {
  limit?: number;
  queryKey: QueryKey;
  queryFn?: ({ pageParam }: { pageParam: number }) => Promise<PaginatedResult<T>>;
  pageSize: number;
  enabled: boolean;
};

export function useInfiniteQueryWithLimit<T>({
  enabled,
  queryKey,
  pageSize,
  queryFn,
  limit,
}: UseInfiniteQueryWithLimitProps<T>) {
  const { error, isFetching, isLoading, data, fetchNextPage, refetch, hasNextPage, isPending } = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      getNextPageParam({
        pageCount: pages.length,
        totalCount: lastPage.totalCount,
        pageSize,
        limit: limit,
      }),
    enabled,
  });

  return {
    data,
    hasNextPage,
    isLoading: isLoading || isFetching || isPending,
    error: error,
    fetchNextPage,
    refetch,
  };
}

type GetNextPageParamArgs = {
  limit?: number;
  pageCount: number;
  pageSize: number;
  totalCount: number;
};

function getNextPageParam({ limit, pageCount, pageSize, totalCount }: GetNextPageParamArgs) {
  const maxPages = getMaxPages({ limit, totalCount, pageSize });

  if (pageCount < maxPages) {
    return pageCount + 1;
  }

  return undefined;
}

function getMaxPages({ limit, pageSize, totalCount }: Omit<GetNextPageParamArgs, 'pageCount'>) {
  const totalPages = Math.ceil(totalCount / pageSize);

  if (!limit) {
    return totalPages;
  }

  const limitPages = Math.ceil(limit / pageSize);

  return Math.min(limitPages, totalPages);
}
